/**
 * BrainSwipe Start Page
 *
 * Выбор категории, тега и старт игры
 * Поддержка URL параметров: ?category=nature&tag=животные
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSwipeStore } from '../stores/swipe';
import { toTranslit } from '../utils/translit';
import { shareResult } from '../lib/share';
import { detectPlatform } from '../platform/detect';

const TOP_TAGS_COUNT = 10; // Показываем топ-10 тегов по умолчанию
const TAGS_PAGE_SIZE = 20; // Подгружаем по 20 тегов

export function BrainSwipeStart() {
  const [searchParams] = useSearchParams();
  const [visibleTagsCount, setVisibleTagsCount] = useState(TOP_TAGS_COUNT);
  const [tagSearch, setTagSearch] = useState('');
  
  const {
    categories,
    selectedCategory,
    tags,
    selectedTag,
    isLoading,
    isLoadingTags,
    error,
    loadCategories,
    loadTags,
    selectCategory,
    selectTag,
    startGame,
  } = useSwipeStore();

  useEffect(() => {
    loadCategories();
    loadTags();
  }, [loadCategories, loadTags]);

  // Читаем фильтры из URL при загрузке
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    const tagFromUrl = searchParams.get('tag');
    
    if (categoryFromUrl && categories.length > 0) {
      const exists = categories.find(c => c.id === categoryFromUrl);
      if (exists) {
        selectCategory(categoryFromUrl);
      }
    }
    
    if (tagFromUrl && tags.length > 0) {
      selectTag(tagFromUrl);
    }
  }, [searchParams, categories, tags, selectCategory, selectTag]);

  // Сбрасываем пагинацию при поиске
  useEffect(() => {
    if (tagSearch) {
      setVisibleTagsCount(TOP_TAGS_COUNT);
    }
  }, [tagSearch]);

  const handleStart = () => {
    startGame();
  };

  const handleShare = () => {
    const platform = detectPlatform();
    
    let url: string;
    let text = "🧠 BrainSwipe";
    
    if (platform === 'vk') {
      // VK Mini App
      const baseUrl = "https://vk.com/app52236695";
      if (selectedTag) {
        const tagSlug = toTranslit(selectedTag);
        url = baseUrl + "#tag=" + tagSlug;
        text = "🏷️ Викторина: " + selectedTag;
      } else if (selectedCategory) {
        url = baseUrl + "#category=" + selectedCategory;
        const cat = categories.find(c => c.id === selectedCategory);
        text = cat ? cat.emoji + " " + cat.name : "📁 " + selectedCategory;
      } else {
        url = baseUrl;
        text = "🧠 BrainSwipe — бесконечная викторина!";
      }
    } else {
      // Telegram / Web
      const baseUrl = "https://t.me/qwizmug_bot?start=";
      if (selectedTag) {
        const tagSlug = toTranslit(selectedTag);
        url = baseUrl + "tag_" + tagSlug;
        text = "🏷️ Викторина: " + selectedTag;
      } else if (selectedCategory) {
        url = baseUrl + "swipe_" + selectedCategory;
        const cat = categories.find(c => c.id === selectedCategory);
        text = cat ? cat.emoji + " " + cat.name : "📁 " + selectedCategory;
      } else {
        url = "https://t.me/qwizmug_bot";
        text = "🧠 BrainSwipe — бесконечная викторина!";
      }
    }
    
    shareResult({ text, url });
  };

  const handleCreateOwn = () => {
    window.location.href = '/?mode=play';
  };

  const handleTagClick = (tagName: string) => {
    if (selectedTag === tagName) {
      selectTag(null);
    } else {
      selectTag(tagName);
      selectCategory(null);
    }
  };

  const handleClearFilters = () => {
    selectCategory(null);
    selectTag(null);
    setTagSearch('');
  };

  const handleShowMore = () => {
    setVisibleTagsCount(prev => prev + TAGS_PAGE_SIZE);
  };

  const handleCollapseTags = () => {
    setVisibleTagsCount(TOP_TAGS_COUNT);
  };

  // Total questions count
  const totalQuestions = categories.reduce((sum, c) => sum + c.questionCount, 0);
  const selectedCategoryInfo = categories.find(c => c.id === selectedCategory);
  const selectedTagInfo = tags.find(t => t.name.toLowerCase() === selectedTag?.toLowerCase());

  // Фильтрация тегов
  const filteredTags = tags.filter(tag => 
    tag.name.toLowerCase().includes(tagSearch.toLowerCase())
  );
  
  // Показываем порцию (при поиске — все результаты)
  const displayedTags = tagSearch 
    ? filteredTags 
    : filteredTags.slice(0, visibleTagsCount);

  // Сколько ещё скрыто
  const remainingTags = filteredTags.length - visibleTagsCount;
  const hasMoreTags = !tagSearch && remainingTags > 0;
  const isExpanded = !tagSearch && visibleTagsCount > TOP_TAGS_COUNT;

  // Есть ли активные фильтры
  const hasActiveFilters = selectedCategory !== null || selectedTag !== null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 via-purple-800 to-indigo-900 text-white p-4 relative">
      {/* Header */}
      <div className="pt-2 pb-4">
        <button
          onClick={() => window.location.href = "/"}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-all mb-4"
        >
          ← Меню
        </button>
        <h1 className="text-3xl font-bold mb-2 text-center">🧠 BrainSwipe</h1>
        <p className="text-purple-200 text-center">Бесконечная лента вопросов</p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-4 text-center">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-4 border-purple-400 border-t-transparent rounded-full mx-auto mb-2"></div>
          <p className="text-purple-300">Загрузка...</p>
        </div>
      )}

      {/* Main Content */}
      {!isLoading && categories.length > 0 && (
        <>
          {/* Active Filters Banner */}
          {hasActiveFilters && (
            <div className="bg-purple-500/30 border border-purple-400/50 rounded-xl p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-purple-200 text-sm">Фильтры:</span>
                  {selectedCategoryInfo && (
                    <span className="bg-purple-600 px-2 py-1 rounded-lg text-sm">
                      {selectedCategoryInfo.emoji} {selectedCategoryInfo.name}
                    </span>
                  )}
                  {selectedTagInfo && (
                    <span className="bg-indigo-600 px-2 py-1 rounded-lg text-sm">
                      🏷️ {selectedTagInfo.name}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleClearFilters}
                  className="text-purple-300 hover:text-white text-sm"
                >
                  ✕ Сбросить
                </button>
              </div>
            </div>
          )}

          {/* Categories */}
          <div className="mb-4">
            <h2 className="text-lg font-semibold mb-3">📁 Категория:</h2>

            {/* All categories button */}
            <button
              onClick={() => selectCategory(null)}
              className={`w-full p-4 rounded-xl mb-3 transition-all ${
                selectedCategory === null
                  ? 'bg-purple-500 ring-2 ring-purple-300'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🎲</span>
                  <span className="font-medium">Все категории</span>
                </div>
                <span className="text-purple-300 text-sm">{totalQuestions} вопросов</span>
              </div>
            </button>

            {/* Category grid */}
            <div className="grid grid-cols-2 gap-3">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => selectCategory(cat.id)}
                  className={`p-4 rounded-xl transition-all text-left ${
                    selectedCategory === cat.id
                      ? 'bg-purple-500 ring-2 ring-purple-300'
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  <div className="text-2xl mb-1">{cat.emoji}</div>
                  <div className="font-medium text-sm">{cat.name}</div>
                  <div className="text-purple-300 text-xs">{cat.questionCount} вопр.</div>
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="mb-4">
              <h2 className="text-lg font-semibold mb-3">🏷️ Тема:</h2>
              
              {/* Tag Search */}
              {tags.length > TOP_TAGS_COUNT && (
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="🔍 Поиск по темам..."
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                </div>
              )}

              {/* Tags Cloud */}
              <div className="flex flex-wrap gap-2">
                {displayedTags.map(tag => (
                  <button
                    key={tag.name}
                    onClick={() => handleTagClick(tag.name)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      selectedTag?.toLowerCase() === tag.name.toLowerCase()
                        ? 'bg-indigo-500 ring-2 ring-indigo-300'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {tag.name}
                    <span className="ml-1 text-purple-300 text-xs">
                      {tag.questionCount}
                    </span>
                  </button>
                ))}
              </div>

              {/* Show More / Collapse */}
              {!tagSearch && filteredTags.length > TOP_TAGS_COUNT && (
                <div className="flex items-center gap-3 mt-3">
                  {hasMoreTags && (
                    <button
                      onClick={handleShowMore}
                      className="text-purple-300 hover:text-white text-sm transition-colors"
                    >
                      ↓ Ещё {Math.min(TAGS_PAGE_SIZE, remainingTags)} из {remainingTags}
                    </button>
                  )}
                  {isExpanded && (
                    <button
                      onClick={handleCollapseTags}
                      className="text-purple-300 hover:text-white text-sm transition-colors"
                    >
                      ↑ Свернуть
                    </button>
                  )}
                </div>
              )}

              {/* No results */}
              {tagSearch && filteredTags.length === 0 && (
                <p className="text-purple-400 text-sm mt-2">
                  Ничего не найдено по запросу "{tagSearch}"
                </p>
              )}

              {/* Loading tags indicator */}
              {isLoadingTags && (
                <div className="text-purple-300 text-sm mt-2">
                  Загрузка тегов...
                </div>
              )}
            </div>
          )}

          {/* Multiplier info */}
          <div className="bg-white/5 rounded-xl p-3 mb-4 text-sm">
            <div className="text-purple-300 mb-2">Множители за серию:</div>
            <div className="flex justify-between text-xs">
              <span>1-4: <span className="text-white">1x</span></span>
              <span>5-9: <span className="text-yellow-400">2x</span></span>
              <span>10-19: <span className="text-orange-400">3x</span></span>
              <span>20+: <span className="text-red-400">5x</span></span>
            </div>
          </div>

          {/* Start & Share buttons */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={handleStart}
              disabled={isLoading}
              className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-98 disabled:opacity-50"
            >
              ▶️ Играть
            </button>
            <button
              onClick={handleShare}
              className="px-5 py-4 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-all active:scale-98"
              title="Поделиться"
            >
              📤
            </button>
          </div>

          {/* Selected filter info */}
          {(selectedCategoryInfo || selectedTagInfo) && (
            <div className="text-center text-purple-300 text-sm mb-4">
              {selectedCategoryInfo && `${selectedCategoryInfo.emoji} ${selectedCategoryInfo.name}`}
              {selectedCategoryInfo && selectedTagInfo && ' • '}
              {selectedTagInfo && `🏷️ ${selectedTagInfo.name}`}
            </div>
          )}

          {/* Create own topic block */}
          <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-400/30 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">🤖</span>
              <span className="font-semibold">Хочешь свою тему?</span>
            </div>
            <p className="text-purple-300 text-sm mb-3">
              AI придумает вопросы на любую тему — фильмы, игры, наука, что угодно!
            </p>
            <button
              onClick={handleCreateOwn}
              className="w-full py-3 bg-white/10 hover:bg-white/20 rounded-lg font-medium transition-all active:scale-98"
            >
              Создать викторину →
            </button>
          </div>
        </>
      )}

      {/* Empty state */}
      {!isLoading && categories.length === 0 && !error && (
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📭</div>
          <p className="text-purple-300">Пока нет вопросов в каталоге</p>
          <p className="text-purple-400 text-sm mt-2">Сыграй несколько викторин, чтобы наполнить каталог!</p>
        </div>
      )}
    </div>
  );
}